/**
 * トースト（画面上中央の共有スタック・等倍で 4 秒静止したのち自動消去）
 *
 * 上中央から小さく透明な状態でスライドインし、拡大しながら定位置で不透明になる。
 * 自動消去時は拡大＋ぼかしで弾ける（burst）退場アニメーションを再生してから DOM を取り除く。
 *
 * 共有スタック（unify-study-toast-stacking design.md D1・D2）:
 *   .lms-toast-host は「汎用トーストと学習中確認トーストを到着順に縦積みするだけ」の
 *   レイアウト要素であり、ARIA ロールを持たない。新しいトーストは常に末尾（＝下）へ積む。
 *   学習中確認トースト（learning-log/dom.ts の syncToast）も ensureToastStack() 経由で
 *   同じスタックへ積むため、積み順は DOM 挿入順で一意に決まる。
 *
 * 読み上げ（同 D1）:
 *   支援技術への通知は、視覚非表示の常設アナウンサー（[data-lms-toast-announcer]、
 *   role="status" / aria-live="polite"）が担う。視覚上のピルは live region の中に入れない
 *   （対話部品を含む学習中トーストが status として読み上げられるのを避けるため）。
 *   学習中確認トーストはアナウンサーを使わず、自要素の ARIA 属性で通知する。
 *
 * 使い方:
 *   - スクリプトから:  window.lmsToast("保存しました", "success")
 *   - サーバーのフラッシュから:
 *       <div data-lms-flash data-lms-flash-type="success">ログインしました</div>
 *     （要素自体は非表示。初期化時に読み取ってトースト表示し、DOM から取り除く）
 */

export type ToastType = "success" | "error" | "info";

/**
 * 自動消去までの時間。showToast() の呼び出し時点から計測するため入場アニメーション（--dur-toast =
 * 0.3s）を含む。等倍で静止している実質の表示時間は 4.0s。
 */
const AUTO_DISMISS_MS = 4300;

/** 退場アニメーションの保険。animationend が発火しない環境でも確実に除去する（--dur-toast=0.3s + 余裕） */
const LEAVE_FALLBACK_MS = 500;

/**
 * 全トースト共通の視覚スタック（.lms-toast-host）を確保する。
 *
 * ARIA ロールは付けない。読み上げは ensureAnnouncer() の live region が担当する
 * （unify-study-toast-stacking design.md D1）。
 * learning-log/dom.ts の syncToast も同じスタックへピルを積むため export している。
 */
export function ensureToastStack(): HTMLElement {
  let stack = document.querySelector<HTMLElement>(".lms-toast-host");
  if (!stack) {
    stack = document.createElement("div");
    stack.className = "lms-toast-host";
    document.body.appendChild(stack);
  }
  return stack;
}

/**
 * 読み上げ専用の常設アナウンサー（視覚非表示の live region）を確保する。
 *
 * 視覚表示はスタック側が担うため、この要素は .lms-visually-hidden で画面から隠す。
 * 一度置いたら取り除かない（app-shell spec「通知用 live region の常設」）。
 */
function ensureAnnouncer(): HTMLElement {
  let announcer = document.querySelector<HTMLElement>("[data-lms-toast-announcer]");
  if (!announcer) {
    announcer = document.createElement("div");
    announcer.className = "lms-visually-hidden";
    announcer.setAttribute("data-lms-toast-announcer", "");
    // 支援技術に通知するが、フォーカスは奪わない
    announcer.setAttribute("role", "status");
    announcer.setAttribute("aria-live", "polite");
    document.body.appendChild(announcer);
  }
  return announcer;
}

/**
 * トーストのアナウンサー（live region）と視覚スタックをアプリ起動時に 1 回だけ作って常設する（部品 #4）。
 *
 * live region は「中身より前に DOM へ存在していること」が前提で、region と中身を同じタスクで
 * 挿入すると NVDA / VoiceOver / JAWS の多くが初期状態とみなして読み上げない（2026-07-27 に
 * MutationObserver で実測: host と中身がともに +54ms）。呼び出し側（main.ts の boot()）は
 * この関数を最初に呼び、最初のトースト生成を別タスクへ回すこと。
 * showToast() 側でも本文追加を別タスクへ回しているため、呼び出し経路によらず保証される。
 */
export function initToastHost(): void {
  ensureAnnouncer();
  ensureToastStack();
}

export function showToast(message: string, type: ToastType = "info"): void {
  if (!message) return;
  const stack = ensureToastStack();
  const announcer = ensureAnnouncer();

  const el = document.createElement("div");
  el.className = `lms-toast lms-toast--${type}`;
  el.textContent = message;
  stack.appendChild(el); // 既存トースト（学習中確認トーストを含む）の下に積む

  // 読み上げ用の 1 行。live region そのものの挿入とは「別のタスク」で追加する（部品 #4）。
  // textContent の上書きではなく子要素の追加にするのは、同じ文言のトーストが連続したときに
  // DOM が変化せず読み上げられなくなるのを防ぐため（従来は region の子としてピルを挿入していた）。
  const announcerLine = document.createElement("div");
  window.setTimeout(() => {
    announcerLine.textContent = message;
    announcer.appendChild(announcerLine);
  }, 0);

  window.setTimeout(() => dismiss(el, announcerLine), AUTO_DISMISS_MS);
}

/** 退場アニメーションを再生してから DOM を取り除く（重複呼び出しは無視） */
function dismiss(el: HTMLElement, announcerLine: HTMLElement): void {
  if (el.classList.contains("is-leaving")) return;
  el.classList.add("is-leaving");

  let removed = false;
  const remove = (): void => {
    if (removed) return;
    removed = true;
    el.remove();
    announcerLine.remove();
    // スタック（.lms-toast-host）とアナウンサー（[data-lms-toast-announcer]）は消さない。
    // アナウンサーを消すと次回また「region と中身を同時挿入」になり
    // 支援技術に読み上げられなくなる（部品 #4）。
  };

  // アニメーション終了で除去。発火しない環境（reduced-motion での即時終了・非表示タブ等）に備えて保険も張る
  el.addEventListener("animationend", remove, { once: true });
  window.setTimeout(remove, LEAVE_FALLBACK_MS);
}

/** サーバーが埋め込んだフラッシュメッセージをトーストに変換する */
export function initFlashToasts(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-flash]").forEach((el) => {
    const message = (el.textContent ?? "").trim();
    const raw = el.dataset["lmsFlashType"];
    const type: ToastType = raw === "success" || raw === "error" ? raw : "info";
    el.remove();
    showToast(message, type);
  });
}

declare global {
  interface Window {
    lmsToast: (message: string, type?: ToastType) => void;
  }
}

export function exposeToastApi(): void {
  window.lmsToast = showToast;
}
